import { __awaiter } from '../../../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { ethers } from 'ethers';
import { DynamicError } from '@dynamic-labs/utils';
import { useInternalDynamicContext } from '../../context/DynamicContext/useInternalDynamicContext.js';
import '@dynamic-labs/sdk-api';
import '../../config/ApiEndpoint.js';
import '../../shared/logger.js';
import { getChainIcon } from '../../shared/utils/functions/chain/getChainIcon.js';
import '@dynamic-labs/wallet-connector-core';
import '@dynamic-labs/wallet-book';
import { shortenWalletAddress } from '../../shared/utils/functions/shortenWalletAddress/shortenWalletAddress.js';
import '../../utils/constants/colors.js';
import '../../shared/utils/classes/storage/localStorage.js';
import { formatBigNumber } from '../../shared/utils/functions/formatBigNumber/formatBigNumber.js';
import '@dynamic-labs/iconic';
import '../../context/ViewContext/ViewContext.js';
import '../../shared/consts/index.js';
import '@dynamic-labs/multi-wallet';
import { getAppOrigin } from '../../utils/functions/getAppOrigin/getAppOrigin.js';
import '@dynamic-labs/rpc-providers';
import '../../utils/hooks/useWallets/utils/verifyMagicIsSetup/verifyMagicIsSetup.js';
import '../../context/ErrorContext/ErrorContext.js';
import '../../context/AccessDeniedContext/AccessDeniedContext.js';
import '../../context/AccountExistsContext/AccountExistsContext.js';
import 'react-dom';
import '../../components/ShadowDOM/ShadowDOM.js';
import '../../context/ThemeContext/ThemeContext.js';
import { Icon } from '../../components/Icon/Icon.js';
import 'react-focus-lock';
import '../../components/Transition/ZoomTransition/ZoomTransition.js';
import '../../components/Transition/SlideInUpTransition/SlideInUpTransition.js';
import '../../components/Transition/OpacityTransition/OpacityTransition.js';
import '../../components/IconButton/IconButton.js';
import '../../components/Alert/Alert.js';
import '../../components/Popper/Popper/Popper.js';
import '../../components/Popper/PopperContext/PopperContext.js';
import { TransactionConfirmationPageLayout } from '../../components/TransactionConfirmationPageLayout/TransactionConfirmationPageLayout.js';
import { TransactionRow } from '../../components/TransactionConfirmationPageLayout/components/TransactionRow/TransactionRow.js';
import 'formik';
import 'yup';
import '../../components/InlineWidget/InlineWidget.js';
import '../../components/MenuList/Dropdown/Dropdown.js';
import { useFetchCurrency } from '../../widgets/DynamicWidget/hooks/useFetchCurrency/useFetchCurrency.js';
import { useTransactionGas } from './hooks/useTransactionGas/useTransactionGas.js';
import { useTransactionTotal } from './hooks/useTransactionTotal/useTransactionTotal.js';
import { transactionErrorMessage } from './helpers/transactionErrorMessage.js';
import '../../context/MockContext/MockContext.js';
import '../../context/CaptchaContext/CaptchaContext.js';
import { usePromise } from '../../utils/hooks/usePromise/usePromise.js';
import { useMutation } from '../../utils/hooks/useMutation/useMutation.js';
import '../../utils/hooks/useUserUpdateRequest/useUserUpdateRequest.js';

const TransactionConfirmationView = ({ provider, transaction, mutation, onError, onSuccess, onClickBack, onClickClose, title, displayPoweredByDynamicFooter = false, }) => {
    const { appName, appLogoUrl, primaryWallet } = useInternalDynamicContext();
    const appOrigin = getAppOrigin();
    if (!primaryWallet) {
        throw new DynamicError('Primary wallet is not available, transaction cannot be displayed');
    }
    const [currencySymbol] = useFetchCurrency(primaryWallet.connector);
    const { data: balance } = usePromise(() => primaryWallet.connector
        .getBalance()
        .then((balanceText) => balanceText ? ethers.utils.parseEther(balanceText) : undefined));
    const { mutate: send, isLoading, error: transactionError, } = useMutation(mutation, {
        onFailure: (error) => onError === null || onError === void 0 ? void 0 : onError(error),
        onSuccess: (tx) => onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(tx),
    });
    const { isLoading: isGasPriceLoading, gasTotalPrice, isGasEstimated, } = useTransactionGas({
        provider,
        transaction,
    });
    const { data: transactionToAddress, isLoading: isTransactionToAddressLoading, } = usePromise(() => transaction.to);
    const { data: transactionFromAddress, isLoading: isTransactionFromAddressLoading, } = usePromise(() => transaction.from);
    const { data: transactionValue, isLoading: isTransactionValueLoading } = usePromise(() => __awaiter(void 0, void 0, void 0, function* () {
        const val = yield transaction.value;
        return val ? ethers.BigNumber.from(val) : undefined;
    }));
    const total = useTransactionTotal({
        gasTotalPrice,
        transactionValue,
    });
    const isTransactionLoading = isTransactionValueLoading ||
        isGasPriceLoading ||
        isTransactionToAddressLoading ||
        isTransactionFromAddressLoading;
    const ChainIconComponent = getChainIcon(primaryWallet.chain);
    const chainIcon = (jsx(Icon, Object.assign({ size: 'small' }, { children: jsx(ChainIconComponent, {}) })));
    const errorMessage = transactionError && transactionErrorMessage(transactionError);
    const missingFunds = useMemo(() => {
        if (balance === undefined || total === undefined)
            return undefined;
        return total.sub(balance);
    }, [balance, total]);
    const hasInsufficientFunds = useMemo(() => {
        if (balance === undefined || total === undefined)
            return false;
        return total.gt(balance);
    }, [balance, total]);
    const warningMessage = useMemo(() => {
        const showInsufficientFundsWarningMessage = hasInsufficientFunds && missingFunds;
        if (!showInsufficientFundsWarningMessage)
            return;
        const amountLeft = formatBigNumber(missingFunds, 5);
        return `Insufficient funds due to gas price increase from estimate. Please add ${amountLeft} ${currencySymbol} to continue.`;
    }, [currencySymbol, hasInsufficientFunds, missingFunds]);
    return (jsxs(TransactionConfirmationPageLayout, Object.assign({ onClickBack: onClickBack, onClickClose: onClickClose, title: title, appOrigin: appOrigin, appName: appName, appLogoUrl: appLogoUrl, isLoading: isLoading, onClickSend: () => send(), displayPoweredByDynamicFooter: displayPoweredByDynamicFooter, disableSendButton: isTransactionLoading || hasInsufficientFunds, error: errorMessage, warningMessage: warningMessage }, { children: [jsx(TransactionRow, Object.assign({ label: 'From (You)', isLoading: isTransactionFromAddressLoading, title: transactionFromAddress }, { children: shortenWalletAddress(transactionFromAddress, 4, 4) })), jsx(TransactionRow, Object.assign({ label: 'To', isLoading: isTransactionToAddressLoading, title: transactionToAddress }, { children: transactionToAddress &&
                    shortenWalletAddress(transactionToAddress, 4, 4) })), jsx(TransactionRow, Object.assign({ label: 'Amount', isLoading: isTransactionValueLoading, isEmpty: !transactionValue, title: transactionValue && formatBigNumber(transactionValue), suffix: currencySymbol, icon: chainIcon, dataTestId: 'transaction-amount' }, { children: transactionValue ? formatBigNumber(transactionValue, 10) : 0 })), jsx(TransactionRow, Object.assign({ label: isGasEstimated ? 'Gas (estimate)' : 'Gas', isEmpty: !gasTotalPrice, isLoading: isGasPriceLoading, title: gasTotalPrice && formatBigNumber(gasTotalPrice), suffix: gasTotalPrice && currencySymbol, icon: gasTotalPrice && chainIcon, dataTestId: isGasEstimated
                    ? 'transaction-estimated-gas-fee'
                    : 'transaction-gas-fee' }, { children: gasTotalPrice ? formatBigNumber(gasTotalPrice, 10) : 'N/A' })), jsx(TransactionRow, Object.assign({ label: 'Total', isEmpty: !total, title: total && formatBigNumber(total), suffix: total && currencySymbol, icon: total && chainIcon, dataTestId: 'transaction-total' }, { children: total ? formatBigNumber(total, 10) : 'N/A' }))] })));
};

export { TransactionConfirmationView };
